import React from 'react';
import { motion } from 'framer-motion';
import { Skill } from '../types/resume.types';

interface SkillPillsProps {
  skills: Skill[];
  maxItems?: number;
  className?: string;
}

const SkillPills: React.FC<SkillPillsProps> = ({ 
  skills, 
  maxItems = 12, 
  className = '' 
}) => {
  // Define priority skills order
  const priorityOrder = [
    // Core skills (top priority)
    'Python', 'Robot Framework', 'Selenium', 'API Testing', 'Postman',
    // Mid-level skills
    'Jenkins', 'Docker', 'Git', 'JMeter', 'AI Prompting',
    // Supporting skills (lower priority)
    'SQL', 'JavaScript', 'TypeScript', 'Playwright'
  ];

  // Extract all skills with categories
  const allSkills = skills.flatMap(category => 
    category.items.map(item => ({
      // Simplify Selenium WebDriver to Selenium
      name: item === 'Selenium WebDriver' ? 'Selenium' : item,
      category: category.category
    }))
  );

  // Add missing trending skills if not present
  const hasAPITesting = allSkills.some(s => s.name === 'API Testing');
  
  if (!hasAPITesting) {
    allSkills.push({ name: 'API Testing', category: 'API Testing' });
  }
  
  // Replace "Prompting Engineering" with "AI Prompting" if exists
  const updatedSkills = allSkills.map(skill => ({
    ...skill,
    name: skill.name === 'Prompting Engineering' ? 'AI Prompting' : skill.name
  }));

  // Sort by priority and take maxItems
  const featuredSkills = updatedSkills
    .sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a.name);
      const bIndex = priorityOrder.indexOf(b.name);
      
      // If both are in priority list, sort by priority
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only a is in priority list, a comes first
      if (aIndex !== -1) return -1;
      // If only b is in priority list, b comes first
      if (bIndex !== -1) return 1;
      // Otherwise maintain original order
      return 0;
    })
    .slice(0, maxItems);

  const scrollToSection = (category: string) => {
    // Scroll to skills section by default, or specialties for certain categories
    const sectionId = category.includes('Test') || category.includes('Performance') || category.includes('API') 
      ? 'specialties' 
      : 'skills';
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20
      }
    }
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className={`flex flex-wrap justify-center gap-3 ${className}`}
    >
      {featuredSkills.map((skill) => (
        <motion.div
          key={`${skill.category}-${skill.name}`}
          variants={item}
          whileHover={{ 
            scale: 1.05, 
            y: -2,
            transition: { duration: 0.2 }
          }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollToSection(skill.category)}
          className="group relative cursor-pointer"
        >
          <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 group-hover:border-primary-300 dark:group-hover:border-primary-500">
            <span className="text-gray-700 dark:text-gray-300 text-sm font-medium group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
              {skill.name}
            </span>
          </div>
          
          {/* Tooltip showing category */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-10 pointer-events-none">
            {skill.category}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default SkillPills;